import React, { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import api from '../../../api/axios';
import { useMicrosoftAuth } from "../../../context/MicrosoftAuthContext";
import { toast } from 'react-toastify';

// Custom tooltip component
const CustomTooltip = ({ active, payload, label, isDark, coordinate }) => {
  if (!active || !payload || !payload.length) return null;

  const value = payload[0].value;
  // Don't show tooltip if value is 0
  if (value === 0) return null;

  // Position tooltip at the top of the bar
  const tooltipStyle = {
    backgroundColor: isDark ? '#1e1e1e' : '#fff',
    border: isDark ? '1px solid #292929' : '1px solid #d9d9d9',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: window.innerWidth < 640 ? '12px' : '14px',
    color: isDark ? '#ffffff' : '#072837',
  };

  return (
    <div style={tooltipStyle}>
      <p style={{ margin: 0, fontWeight: 'bold' }}>
        {label.charAt(0).toUpperCase() + label.slice(1)}
      </p>
      <p style={{ margin: '4px 0 0 0' }}>
        Submissions: {value}
      </p>
    </div>
  );
};

const isMonthlyView = (range) => range === 'This Month' || range === 'Last Month';

const FixedWidthTick = ({ x, y, payload, fill, fontSize }) => {
  if (!payload || !payload.value) return null;

  let displayValue = payload.value;
  const parts = payload.value.split(' ');
  if (parts.length === 2 && parts[1].length === 1) {
    displayValue = `${parts[0]} 0${parts[1]}`;
  }

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="middle"
        fill={fill}
        fontSize={fontSize}
        style={{ fontFamily: 'monospace' }}
      >
        {displayValue}
      </text>
    </g>
  );
};

export function Chart({ timeRange = 'This Year', periodOffset = 0, isDark = false }) {
  const { accessToken, jwtToken, userTeamData } = useMicrosoftAuth();
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [yMax, setYMax] = useState(1);
  const [yTicks, setYTicks] = useState([1]);


  const computeNiceScale = (maxValue, desiredTickCount = 5) => {
    const niceNumber = (value, round) => {
      const exponent = Math.floor(Math.log10(value));
      const fraction = value / Math.pow(10, exponent);
      let niceFraction;
      if (round) {
        if (fraction < 1.5) niceFraction = 1;
        else if (fraction < 3) niceFraction = 2;
        else if (fraction < 7) niceFraction = 5;
        else niceFraction = 10;
      } else {
        if (fraction <= 1) niceFraction = 1;
        else if (fraction <= 2) niceFraction = 2;
        else if (fraction <= 5) niceFraction = 5;
        else niceFraction = 10;
      }
      return niceFraction * Math.pow(10, exponent);
    };

    if (!Number.isFinite(maxValue) || maxValue <= 0) {
      return { max: 1, ticks: [1] };
    }

    const range = niceNumber(maxValue, false);
    const tickSpacing = Math.max(1, Math.round(niceNumber(range / (desiredTickCount - 1), true)));
    const niceMax = Math.ceil(maxValue / tickSpacing) * tickSpacing;

    const ticks = [];
    for (let t = niceMax; t > 0; t -= tickSpacing) {
      // Always use integers only
      ticks.push(Math.round(t));
      if (ticks.length >= desiredTickCount) break;
    }
    return { max: Math.round(niceMax), ticks };
  };

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = jwtToken || localStorage.getItem('jwtToken') || accessToken || localStorage.getItem('msalToken');
        if (!token) {
          console.error('No token available for chart data API request');
          setError('Authentication token missing');
          setLoading(false);
          return;
        }

        console.log('Fetching chart data with range:', timeRange);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;


        const data = new FormData();
        data.append('user_team_id', userTeamData?.id);
        data.append('user_role_id', userTeamData?.user_role_id);
        userTeamData?.user_group?.forEach(group => {
            data.append('user_group[]', group);
        });
        
        const response = await api.post(`/submissions/dashboard/stats?range=${timeRange}&offset=${periodOffset}`, data);
        const { chart } = response.data.data;

        if (!chart || !chart.labels || !chart.data) {
          console.error('Invalid chart data format:', chart);
          setError('Invalid chart data format received from the server');
          return;
        }

        // Convert { labels: [...], data: [...] } to [{ month: 'Jan', value: 10 }, ...]
        const transformedData = chart.labels.map((label, index) => ({
          month: label,
          value: chart.data[index] || 0
        }));

        setChartData(transformedData);

        const maxVal = Math.max(0, ...transformedData.map(d => d.value || 0));
        const { max: niceMax, ticks } = computeNiceScale(maxVal, 5);
        setYMax(niceMax);
        setYTicks(ticks);
      } catch (error) {
        console.error('Error fetching chart data:', error);
        setError('Failed to load chart data. Please try again later.');

        if (error.response) {
          console.error('Chart API error response:', error.response.status, error.response.data);

          if (error.response.status === 401) {
            console.log('Unauthorized access in chart component, redirecting to login');
            toast.error('Your session has expired. Please login again.');
            setTimeout(() => {
              window.location.href = '/login';
            }, 2000);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [timeRange, periodOffset, accessToken, jwtToken, userTeamData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[257px]">
        <div className="text-[14px] transition-colors duration-200" style={{ color: isDark ? '#b0b0b0' : '#686868' }}>
          Loading chart data...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[257px] text-red-600">
        <div className="text-[14px]">{error}</div>
      </div>
    );
  }

  return (
    <div className="w-full flex gap-[2px]">
      <div
        className="relative text-right text-[12px] sm:text-[14px] font-normal transition-colors duration-200 pr-2"
        style={{
          color: isDark ? '#b0b0b0' : '#686868',
          height: window.innerWidth < 640 ? '200px' : '257px',
          paddingTop: '20px',
          paddingBottom: '20px',
          width: '40px', // Fixed width to contain the numbers
          flexShrink: 0 // Prevent the container from shrinking
        }}
      >
        {[...yTicks, 0].map((val, idx) => {
          // Position each tick based on its value relative to yMax
          // Account for padding to keep ticks within bounds
          const availableHeight = (window.innerWidth < 640 ? 200 : 257) - 40; // subtract padding
          const positionFromTop = ((yMax - val) / yMax) * availableHeight;
          return (
            <div
              key={idx}
              className="absolute right-0 flex items-center justify-end leading-none"
              style={{ top: `${positionFromTop}px` }}
            >
              {Math.round(val)}
            </div>
          );
        })}
      </div>

      {/* Chart Area with axes */}
      <div className={`flex-1 flex flex-col gap-[10px] overflow-x-auto ${isDark ? '[&::-webkit-scrollbar-track]:bg-[#000000] [&::-webkit-scrollbar-thumb]:bg-[#292929] [&::-webkit-scrollbar-thumb]:hover:bg-[#4d4d4d] [&::-webkit-scrollbar-button]:bg-[#161515]' : '[&::-webkit-scrollbar-track]:bg-[#f5f5f5] [&::-webkit-scrollbar-thumb]:bg-[#ebebeb] [&::-webkit-scrollbar-thumb]:hover:bg-[#e5e5e5] [&::-webkit-scrollbar-button]:bg-[#d9d9d9]'} [&::-webkit-scrollbar]:h-[10px] [&::-webkit-scrollbar]:w-[16px] [&::-webkit-scrollbar-button]:w-[16px] [&::-webkit-scrollbar-button]:h-[18px]`}>
        {isMonthlyView(timeRange) ? (
          <div style={{ minWidth: `${chartData.length * 50}px`, height: window.innerWidth < 640 ? 200 : 257 }}>
            <ResponsiveContainer width="100%" height="100%" debounce={150}>
              <BarChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="0" stroke="transparent" />
                <XAxis
                  dataKey="month"
                  tick={<FixedWidthTick fill={isDark ? '#b3b3b3' : '#686868'} fontSize={window.innerWidth < 640 ? 11 : 14} />}
                  tickLine={false}
                  axisLine={{ stroke: isDark ? '#ffffff' : '#072837', strokeWidth: 1 }}
                  interval={0}
                />
                <YAxis
                  width={8}
                  tick={false}
                  tickLine={false}
                  axisLine={{ stroke: isDark ? '#ffffff' : '#072837', strokeWidth: 1 }}
                  domain={[0, yMax]}
                />
                <Tooltip
                  content={<CustomTooltip isDark={isDark} />}
                  cursor={false}
                  position={(tooltipProps) => {
                    if (tooltipProps && tooltipProps.viewBox) {
                      return {
                        x: tooltipProps.viewBox.x + tooltipProps.viewBox.width / 2,
                        y: tooltipProps.viewBox.y - 10
                      };
                    }
                    return { x: 0, y: 0 };
                  }}
                  wrapperStyle={{ pointerEvents: 'none' }}
                  isAnimationActive={false}
                />
                <Bar
                  dataKey="value"
                  fill={isDark ? '#B3886B' : '#072837'}
                  radius={[32, 32, 0, 0]}
                  maxBarSize={window.innerWidth < 640 ? 28 : 39}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={window.innerWidth < 640 ? 200 : 257} debounce={150}>
            <BarChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="0" stroke="transparent" />
              <XAxis
                dataKey="month"
                tick={{ fill: isDark ? '#b3b3b3' : '#686868', fontSize: window.innerWidth < 640 ? 11 : 14 }}
                tickLine={false}
                axisLine={{ stroke: isDark ? '#ffffff' : '#072837', strokeWidth: 1 }}
                tickMargin={window.innerWidth < 640 ? 6 : 10}
                interval={0}
              />
              <YAxis
                width={8}
                tick={false}
                tickLine={false}
                axisLine={{ stroke: isDark ? '#ffffff' : '#072837', strokeWidth: 1 }}
                domain={[0, yMax]}
              />
              <Tooltip
                content={<CustomTooltip isDark={isDark} />}
                cursor={false}
                position={(tooltipProps) => {
                  if (tooltipProps && tooltipProps.viewBox) {
                    return {
                      x: tooltipProps.viewBox.x + tooltipProps.viewBox.width / 2,
                      y: tooltipProps.viewBox.y - 10
                    };
                  }
                  return { x: 0, y: 0 };
                }}
                wrapperStyle={{ pointerEvents: 'none' }}
                isAnimationActive={false}
              />
              <Bar
                dataKey="value"
                fill={isDark ? '#B3886B' : '#072837'}
                radius={[32, 32, 0, 0]}
                maxBarSize={window.innerWidth < 640 ? 28 : 39}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

